import React, { useEffect, useState } from "react";
import { Button, Table, Form, FloatingLabel, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faEdit } from "@fortawesome/free-solid-svg-icons";
import { Loading, AppPagination } from "../../components/Index.js";
import AddAshaBlock from "./AddAshaBlock.js";
import EditAshaBlock from "./EditAshaBlock.js";
import { AshaBlockServices } from "../../../services/AshaBlocks.js";
import { PHCServices } from "../../../services/Phcs.js";
import { TalukServices } from "../../../services/Taluk.js";
import AppNav from "../../../header/AppNav";
import ConfirmModal from "../../../components/ConfirmModal";
import AlertModal from "../../../components/AlertModal";

function AshaBlock() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const [curPage, setCurPage] = useState(1);
  const pageSize = 10;

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [editModalShow, setEditModalShow] = useState(false);
const [selectedAshaBlock, setSelectedAshaBlock] = useState(null);
  const [confirmModalData, setConfirmModalData] = useState({
    title: "",
    message: "",
    onConfirm: null,
  });
  const [alertModalData, setAlertModalData] = useState({
    title: "",
    message: "",
    type: "info",
  });

  const [ashaBlocks, setAshaBlocks] = useState([]);
  const [phcs, setPhcs] = useState([]);
  const [taluks, setTaluks] = useState([]);

  const [selectedTalukId, setSelectedTalukId] = useState("");
  const [selectedPhcId, setSelectedPhcId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // 🔹 Fetch and enrich data
  const getData = async () => {
    setLoading(true);
    try {
      const [ashaRes, phcRes, talukRes] = await Promise.all([
        AshaBlockServices.getAllAshaBlocks(),
        PHCServices.getAllPhcs(),
        TalukServices.getAllTaluks(),
      ]);

      const enriched = (Array.isArray(ashaRes) ? ashaRes : []).map((block) => {
        const phc = phcRes.find((p) => p.id === block.phcId) || {};
        const taluk = talukRes.find((t) => t.id === phc.talukId) || {};
        return { ...block, phcDetails: phc, talukDetails: taluk };
      });

      setAshaBlocks(enriched);
      setPhcs(Array.isArray(phcRes) ? phcRes : []);
      setTaluks(Array.isArray(talukRes) ? talukRes : []);
    } catch (error) {
      console.error("Error fetching ashaBlocks/phcs/taluks:", error);
      setAshaBlocks([]);
      setPhcs([]);
      setTaluks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  // 🔹 Delete handling
  const handleDeleteClick = (id, name) => {
    setConfirmModalData({
      title: "Confirm Delete",
      message: `Are you sure you want to delete \"${name}\"?`,
      onConfirm: () => confirmDeleteAshaBlock(id),
    });
    setShowConfirmModal(true);
  };

  const confirmDeleteAshaBlock = async (id) => {
    setShowConfirmModal(false);
    setLoading(true);
    try {
      const res = await AshaBlockServices.deleteAshaBlock(id);
      if (res.status === 200) {
        setAlertModalData({
          title: "Success",
          message: res.message || "Asha Block deleted successfully!",
          type: "success",
        });
        setShowAlertModal(true);
        getData();
      } else {
        setAlertModalData({
          title: "Error",
          message: "Error while deleting Asha Block",
          type: "error",
        });
        setShowAlertModal(true);
      }
    } catch (error) {
      setAlertModalData({
        title: "Error",
        message: error.message || "Failed to delete Asha Block",
        type: "error",
      });
      setShowAlertModal(true);
    } finally {
      setLoading(false);
    }
  };

  const filteredAshaBlocks = ashaBlocks
    .filter((item) =>
      selectedTalukId
        ? item.talukDetails?.id === parseInt(selectedTalukId)
        : true
    )
    .filter((item) =>
      selectedPhcId ? item.phcId === parseInt(selectedPhcId) : true
    )
    .filter((item) => {
      const ashaBlockLower = item.ashaBlock?.toLowerCase() || "";
      const phcLower = item.phcDetails?.phc?.toLowerCase() || "";
      const talukLower = item.talukDetails?.taluk?.toLowerCase() || "";
      const searchTermLower = searchTerm.toLowerCase();

      return (
        ashaBlockLower.includes(searchTermLower) ||
        phcLower.includes(searchTermLower) ||
        talukLower.includes(searchTermLower)
      );
    });

  const maxPage = Math.ceil(filteredAshaBlocks.length / pageSize) || 1;
  const paginatedAshaBlocks = filteredAshaBlocks.slice(
    (curPage - 1) * pageSize,
    curPage * pageSize
  );

  useEffect(() => {
    setCurPage(1);
  }, [searchTerm, selectedTalukId, selectedPhcId]);

  return (
    <div>
      <AppNav />
      <h1 className="list-style">Asha Blocks</h1>
      <br />
      <div className="list-table-style">
        {/* 🔹 Filters + Add button */}
        <Row className="align-items-end g-2 mb-3">
          <Col md={3}>
            <FloatingLabel controlId="Taluk" label="Taluk">
              <Form.Select
                value={selectedTalukId}
                onChange={(e) => {
                  setSelectedTalukId(e.target.value);
                  setSelectedPhcId("");
                }}
                style={{ height: "calc(3.5rem + 2px)" }}
              >
                <option value="">All Taluks</option>
                {taluks.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.taluk}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </Col>

          <Col md={3}>
            <FloatingLabel controlId="Phc" label="PHC">
              <Form.Select
                value={selectedPhcId}
                onChange={(e) => setSelectedPhcId(e.target.value)}
                style={{ height: "calc(3.5rem + 2px)" }}
              >
                <option value="">All PHCs</option>
                {phcs
                  .filter((p) =>
                    selectedTalukId ? p.talukId === parseInt(selectedTalukId) : true
                  )
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.phc}
                    </option>
                  ))}
              </Form.Select>
            </FloatingLabel>
          </Col>

          <Col md={3}>
            <FloatingLabel controlId="search" label="Search by AshaBlock / PHC / Taluk">
              <Form.Control
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </FloatingLabel>
          </Col>

          <Col md={3} className="d-flex justify-content-end">
            <Button
              onClick={() => setShowAddModal(true)}
              style={{ height: "calc(3.5rem + 2px)" }}
            >
              Add Asha Block
            </Button>
            <AddAshaBlock
  show={showAddModal}
  onHide={() => setShowAddModal(false)}
  taluks={taluks}
  phcs={phcs}
/>

          </Col>
        </Row>

        {/* 🔹 Table */}
        <Table striped bordered hover className="professional-table">
          <thead>
            <tr>
              <th>Sl no</th>
              <th className="list-item-align-center">Asha Block</th>
              <th className="list-item-align-center">Taluk</th>
              <th className="list-item-align-center">PHC</th>
              <th className="list-item-align-center">Edit</th>
              {/* <th className="list-item-align-center">Delete</th> */}
            </tr>
          </thead>
          <tbody>
            {!loading &&
              paginatedAshaBlocks.map((i, index) => (
                <tr key={i.id}>
                  <td>{(curPage - 1) * pageSize + index + 1}</td>
                  <td className="list-item-align-center">{i.ashaBlock}</td>
                  <td className="list-item-align-center">
                    {i.talukDetails?.taluk || "N/A"}
                  </td>
                  <td className="list-item-align-center">
                    {i.phcDetails?.phc || "N/A"}
                  </td>
                  <td className="list-item-align-center">
                  <td className="list-item-align-center">
  <FontAwesomeIcon
    icon={faEdit}
    style={{ color: '#05275a' }}
    size="lg"
    className="action-icon edit-icon"
    title="Edit Asha Block"
    onClick={() => {
      setSelectedAshaBlock(i);
      setEditModalShow(true);
    }}
  />
</td>
                  </td>
                  {/* <td className="list-item-align-center">
                    <FontAwesomeIcon
                      icon={faTrash}
                      size="lg"
                      className="action-icon delete-icon"
                      title="Delete Asha Block"
                      onClick={() => handleDeleteClick(i.id, i.ashaBlock)}
                    />
                  </td> */}
                </tr>
              ))}
          </tbody>
        </Table>
      </div>

      {filteredAshaBlocks.length < 1 && !loading && (
        <h3 style={{ color: "#e77225", textAlign: "center" }}>List is empty</h3>
      )}
      {loading && <Loading />}

      <div className="d-flex justify-content-center my-3">
        <AppPagination curPage={curPage} maxPage={maxPage} changePage={setCurPage} />
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        show={showConfirmModal}
        title={confirmModalData.title}
        message={confirmModalData.message}
        onConfirm={confirmModalData.onConfirm}
        onHide={() => setShowConfirmModal(false)}
      />

      {/* Alert Modal */}
      <AlertModal
        show={showAlertModal}
        title={alertModalData.title}
        message={alertModalData.message}
        type={alertModalData.type}
        onHide={() => setShowAlertModal(false)}
      />
      <EditAshaBlock
  show={editModalShow}
  onHide={() => setEditModalShow(false)}
  ashaBlock={selectedAshaBlock}
  taluks={taluks}
  phcs={phcs}
  onUpdated={() => getData()}
/>
    </div>
  );
}

export default AshaBlock;
